import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import AppButton from '@/components/controls/AppButton';
import { useHistory } from '@/modules/core/hooks';
import useRegistrationVerificationQuery from '@/modules/user/components/SignUp/ProfileInformation/queries/useRegistrationVerification.query';
import { useProfileInformationFormStore } from '@/modules/user/components/SignUp/ProfileInformation/stores/useProfileInformationFormMode.store';
import { useStore as useProfileInformationStore } from '@/modules/user/components/SignUp/ProfileInformation/useProfileInformation.store';

const StyledList = styled(Box)`
  font-size: 14px;
  ol, ul {
    list-style: auto;
    margin-left: 22px;
  }
  ul {
    list-style: disc;
    margin-bottom: ${({ theme }) => theme.spacings.spacing8};
  }
`;

interface Props {
  onValidation: () => boolean;
}

const EditProfileInformationButton: React.FC<Props> = ({ onValidation }) => {
  const { t } = useTranslation();
  const {
    values: { isFormEditMode, isFormError },
    handleSetIsFormEditMode,
    handleSetIsFormError,
  } = useProfileInformationFormStore();
  const profileInformation = useProfileInformationStore((state) => state.state);

  const { registrationVerification, isLoading } = useRegistrationVerificationQuery();

  const handleScroll = useCallback(() => {
    setTimeout(() => {
      const accountAccessBlock = document.getElementById('account_access_block_wrapper');
      accountAccessBlock?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
  }, []);

  const handleVerification = useCallback(async () => {
    if (isFormEditMode) {
      try {
        const isSuccessValidation = onValidation();
        if (isSuccessValidation) {
          const response = await registrationVerification({
            birthOn: profileInformation.birthdate,
            uniqueId: profileInformation.uniqueId,
          });
          if (!response.employee_id) {
            throw new Error();
          }
          handleSetIsFormError(false);
          handleSetIsFormEditMode(false);
          handleScroll();
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        handleSetIsFormError(true);
      }
    } else {
      handleSetIsFormEditMode(true);
    }
  }, [
    isFormEditMode,
    profileInformation,
    handleSetIsFormEditMode,
    handleSetIsFormError,
    registrationVerification,
    handleScroll,
    onValidation,
  ]);
  const history = useHistory();

  return (
    <Box width="100%">
      {
        isFormError && (
          <Box>
            <Inscription color="danger" margin={{ bottom: 'spacing8' }}>
              {t('We couldn’t register your account with the information provided.')}
            </Inscription>
            <StyledList margin={{ bottom: 'spacing16' }}>
              <ul>
                <li>
                  {t('Already registered?')}&nbsp;
                  <Inscription
                    data-testId="log-in-link"
                    color="textAccent"
                    cursor="pointer"
                    onClick={() => history.push(ROUTES.LOGIN)}
                  >
                    {t('Log In')}
                  </Inscription>
                </li>
                <li>{t('Not registered yet? Try entering your information again.')}</li>
              </ul>
              <Inscription>{t('Still having trouble? Contact your plan administrator for help.')}</Inscription>
            </StyledList>
          </Box>
        )
      }
      <AppButton size="L" width="100%" onClick={handleVerification}>
        {
          isLoading ? (
            <Preloader size={16} thinSize={4} color="white" />
          ) : (
            <Inscription>
              {isFormEditMode 
                ? t('Next') 
                : t('Edit Profile Information')}
            </Inscription>
          )
        }
      </AppButton>
    </Box>
  );
};

export default EditProfileInformationButton;
